import { LogOut, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";

const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" {...props}>
    <path fill="#4285F4" d="M23.52 12.27c0-.86-.08-1.5-.24-2.16H12v3.92h6.6a5.66 5.66 0 0 1-2.45 3.71v3.08h3.96c2.32-2.14 3.41-5.28 3.41-8.55z" />
    <path fill="#34A853" d="M12 24c3.31 0 6.09-1.1 8.11-2.98l-3.96-3.08c-1.1.74-2.5 1.18-4.15 1.18-3.19 0-5.9-2.16-6.86-5.06H1.06v3.18A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.14 14.06a7.2 7.2 0 0 1 0-4.12V6.76H1.06a12 12 0 0 0 0 10.48l4.08-3.18z" />
    <path fill="#EA4335" d="M12 4.77c1.8 0 3.41.62 4.68 1.83l3.51-3.51C18.09 1.15 15.31 0 12 0A12 12 0 0 0 1.06 6.76l4.08 3.18C6.1 6.93 8.81 4.77 12 4.77z" />
  </svg>
);

const initialsOf = (name) =>
  (name || "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "NC";

export const AuthControls = ({ fullWidth = false }) => {
  const { user, loading, login, logout } = useAuth();

  if (loading) {
    return <Skeleton className={fullWidth ? "h-10 w-full rounded-md" : "h-9 w-24 rounded-md"} />;
  }

  if (!user) {
    return (
      <Button
        variant="outlineNavy"
        size={fullWidth ? "lg" : "sm"}
        onClick={login}
        className={fullWidth ? "w-full" : ""}
        data-testid="google-signin-btn"
      >
        <GoogleIcon /> Sign in with Google
      </Button>
    );
  }

  if (fullWidth) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user.picture} alt={user.name || user.email} />
            <AvatarFallback className="bg-primary/10 text-primary">{initialsOf(user.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{user.name || "Signed in"}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={logout} aria-label="Sign out" data-testid="logout-btn-mobile">
          <LogOut className="!size-5" />
        </Button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-1 pr-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="user-menu-trigger"
        >
          <Avatar className="h-7 w-7">
            <AvatarImage src={user.picture} alt={user.name || user.email} />
            <AvatarFallback className="bg-primary/10 text-xs text-primary">{initialsOf(user.name)}</AvatarFallback>
          </Avatar>
          <span className="max-w-[120px] truncate text-sm font-semibold text-foreground">
            {(user.name || user.email || "").split(" ")[0]}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-muted-foreground" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{user.name}</p>
            <p className="truncate text-xs font-normal text-muted-foreground">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
